import AnnouncementController from '@/controllers/admin/announcements';
import router from '@/router'
import {
  AnnouncementAdminResponse,
  AnnouncementDisplayOption,
  AnnouncementType,
  Translation
} from '@/interfaces/admin/announcement'
import { AxiosProgressEvent } from 'axios'
import { cloneDeep } from 'lodash';
import { isValidHex } from '@/helpers'
import { Locale } from '@/interfaces/global'
import { Module } from 'vuex';
import {
  useUploadProgressComputation
} from '@/components/common/upload-progress/useUploadProgressComputation'

// Default values for a new announcement
const defaultAnnouncement: AnnouncementAdminResponse = {
  id: null, // Will be assigned on save for new items
  title: '',
  type: AnnouncementType.NONE,
  displayOption: null,
  fromDate: Date.now(),
  toDate: null,
  enabled: false,
  barColor: null,
  textColor: null,
  externalUrl: null,
  targetMediaIdentifier: null,
  assignedOrganizations: [],
  translations: [] // Requires at least one translation eventually
};

export interface AnnouncementEditorState {
  announcement: AnnouncementAdminResponse | null;
  originalAnnouncement: AnnouncementAdminResponse | null; // For dirty checking
  isDirty: boolean;
  isLoading: boolean;
  isSaving: boolean;
  isUploading: boolean; // Track upload status
  remountKey: number
  uploadedFile: File | null; // For file upload
  uploadProgress: number | null; // Track upload percentage (0-100)
}

const initialState: AnnouncementEditorState = {
  announcement: null,
  originalAnnouncement: null,
  isDirty: false,
  isLoading: false,
  isSaving: false,
  isUploading: false, // Initial upload state
  remountKey: 0, // key to ensure re-mount after reverts and save
  uploadedFile: null,
  uploadProgress: null // Initial upload progress
};

const announcementEditor: Module<AnnouncementEditorState, any> = {
  namespaced: true,
  state: { ...initialState },

  getters: {
    currentAnnouncement: (state) => state.announcement,
    originalAnnouncement: (state) => state.originalAnnouncement,
    isLoading: (state) => state.isLoading,
    isSaving: (state) => state.isSaving,
    assignedOrganizations: (state): number[] => {
      return state.announcement?.assignedOrganizations ?? [];
    },
    availableLocales: (state, rootState) => {
      if (!state.announcement || !state.announcement.translations) {
        return rootState.locales.items || []; // Return all locales if no announcement/translations
      }
      const usedLocaleIds = new Set(state.announcement.translations.map(t => t.localeId));
      const allLocales: Locale[] = rootState.locales.items || [];
      return allLocales.filter(locale => !usedLocaleIds.has(locale.id));
    },
    canSave: (state, getters): boolean => {
      if (!state.announcement || state.isSaving) {
        return false;
      }
      // Basic validation: title required, at least one translation required
      const hasDisplayOption = !!state.announcement.displayOption;
      const hasTitle = !!state.announcement.title;
      const hasTranslation = state.announcement.translations.length > 0 &&
        state.announcement.translations.every(t => t.title.length > 0);
      const hasValidDates = () => {
        if (!!state.announcement && !!state.announcement.toDate) {
          return state.announcement.fromDate < state.announcement.toDate
        }
        return true
      }
      const hasValidBarColor = state.announcement.barColor !== null ?
        isValidHex(state.announcement.barColor) : true;
      const hasValidTextColor = state.announcement.textColor !== null ?
        isValidHex(state.announcement.textColor) : true;

      let hasRequiredFieldsForType = false;
      switch (state.announcement.type) {
        case AnnouncementType.EXTERNAL:
          hasRequiredFieldsForType = !getters.isExternalUrlBad;
          break;
        case AnnouncementType.FILE:
          hasRequiredFieldsForType = !!state.uploadedFile || !!state.announcement.attachmentName;
          break
        case AnnouncementType.MEDIA:
          hasRequiredFieldsForType = !!state.announcement.targetMediaIdentifier;
          break;
        case AnnouncementType.NONE:
          hasRequiredFieldsForType = true;
          break
      }

      return hasDisplayOption && hasRequiredFieldsForType && hasTitle && hasValidDates() &&
        hasTranslation && hasValidBarColor && hasValidTextColor && getters.isDirty;
    },
    displayedFilename: (state): string | null => {
      if (!!state.announcement) {
        return state.uploadedFile?.name ?? state.announcement?.attachmentName ?? null;
      } else {
        return null;
      }
    },
    isExternalUrlBad: (state): boolean => {
      if (!!state.announcement) {
        try {
          const url = new URL(state.announcement?.externalUrl ?? '')
          return !(url.protocol === 'http:' || url.protocol === 'https:')
        } catch (e) {
          return state.announcement.type === AnnouncementType.EXTERNAL
        }
      }

      return false
    },
    isDirty: (state) => state.isDirty,
    isUploading: (state) => state.isUploading,
    selectedType: (state): AnnouncementType | null => {
      return state.announcement?.type ?? null;
    },
    uploadProgress: (state) => state.uploadProgress
  },

  mutations: {
    REMOVE_TRANSLATION(state, index: number) {
      if (state.announcement?.translations && index >= 0 && index < state.announcement.translations.length) {
        state.announcement.translations.splice(index, 1);
        state.isDirty = true;
      }
    },
    RESET_ADDITIONAL_TYPE_DATA(state) {
      if (state.announcement) {
        state.announcement.attachmentName = null;
        state.announcement.externalUrl = null;
        state.announcement.targetMediaIdentifier = null;
        state.uploadedFile = null;
        state.isDirty = true;
        state.uploadedFile = null;
      }
    },
    RESET_STATE(state) {
      Object.assign(state, cloneDeep(initialState));
    },
    RESET_UPLOAD_STATE(state) {
      state.isUploading = false;
      state.uploadProgress = null;
    },
    SET_ANNOUNCEMENT(state, announcement: AnnouncementAdminResponse) {
      state.announcement = cloneDeep(announcement);
      state.isDirty = false;
      state.originalAnnouncement = cloneDeep(announcement);
      state.uploadedFile = null;
    },
    SET_ASSIGNED_ORGANIZATIONS(state, orgIds: number[]) {
      if (state.announcement) {
        state.announcement.assignedOrganizations = orgIds;
        state.isDirty = true;
      }
    },
    SET_EXTERNAL_URL(state, url: string | null) {
      if (state.announcement) {
        state.announcement.externalUrl = url;
        state.announcement.targetMediaIdentifier = null;
        state.isDirty = true;
        state.uploadedFile = null;
      }
    },
    SET_IS_UPLOADING(state, uploading: boolean) {
      state.isUploading = uploading;
    },
    SET_LOADING(state, isLoading: boolean) {
      state.isLoading = isLoading;
    },
    SET_SAVING(state, isSaving: boolean) {
      state.isSaving = isSaving;
      if (!isSaving) {
        state.isDirty = false;
      }
    },
    SET_SELECTED_MEDIA(state, mediaIdentifier: string | null) {
      if (state.announcement) {
        state.announcement.targetMediaIdentifier = mediaIdentifier
        state.announcement.externalUrl = null;
        state.isDirty = true;
        state.uploadedFile = null;
      }
    },
    SET_UPLOAD_PROGRESS(state, progress: number | null) {
      state.uploadProgress = progress;
    },
    SET_UPLOADED_FILE(state, file: File | null) {
      if (state.announcement) {
        state.announcement.externalUrl = null // Clear conflicting fields
        state.announcement.targetMediaIdentifier = null;
        state.isDirty = true;
        state.uploadedFile = file;
      }
    },
    TRIGGER_RERENDER(state) {
      state.remountKey++;
    },
    UPDATE_ANNOUNCEMENT_FIELD(state, { field, value }) {
      if (state.announcement) {
        (state.announcement as any)[field] = value;

        // Determine if this update is one of the initial default color settings
        const isDefaultBarColorInit = field == 'barColor' &&
          state.announcement.barColor == null && state.announcement.barColor == state.originalAnnouncement?.barColor;
        const isDefaultTextColorInit = field == 'textColor' &&
          state.announcement.textColor == null && state.announcement.textColor == state.originalAnnouncement?.textColor;

        // Set dirty unless it's one of the default color initializations
        if (!(isDefaultBarColorInit || isDefaultTextColorInit)) {
          state.isDirty = true;
        }
      }
    },
    // Handles adding or updating a translation based on localeId
    UPDATE_TRANSLATION(state, translation: Translation) {
      if (!state.announcement) return;

      const index = state.announcement.translations
        .findIndex(t => t.localeId == translation.localeId);
      const isInitialTranslation = state.originalAnnouncement?.translations?.length == 0 &&
        state.announcement.translations.length == 0

      // Set dirty unless this is an initial translation
      if (!isInitialTranslation) {
        state.isDirty = true;
      }
      if (index > -1) {
        state.announcement.translations[index] = translation;
      } else {
        state.announcement.translations.push(translation);
      }
    },
    UPDATE_TRANSLATION_TITLE(state, { index, title }: { index: number, title: string }) {
      if (state.announcement && state.announcement.translations[index]) {
        state.announcement.translations[index].title = title;
        state.isDirty = true;
      }
    },
    UPDATE_TRANSLATION_LOCALE(state, { index, localeId }: { index: number, localeId: number }) {
      if (state.announcement && state.announcement.translations[index]) {
        state.announcement.translations[index].localeId = localeId;
        state.isDirty = true;
      }
    }
  },

  actions: {
    async assignAllOrganizations({ state, commit }) {
      if (!state.announcement?.id) {
        console.error("Cannot assign all organizations: Announcement id is missing.");
        throw new Error('Missing announcement id')
      }
      commit('SET_SAVING', true);

      try {
        const updatedAnnouncement = await AnnouncementController.assignAllOrganizations(state.announcement.id!!);
        commit('SET_ANNOUNCEMENT', updatedAnnouncement); // Update the entire announcement state
        commit('TRIGGER_RERENDER');
        await new Promise(resolve => setTimeout(resolve, 250));
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Failed to assign all organizations';
        console.error('Assign All Organizations Error:', message, error);
        throw error;
      } finally {
        commit('SET_SAVING', false);
      }
    },
    async initializeNew({ commit, rootGetters }) {
      commit('SET_LOADING', true);
      commit('RESET_STATE');
      commit('SET_ANNOUNCEMENT', {
        ...defaultAnnouncement,
        barColor: rootGetters['theme/bannersColor'],
        textColor: rootGetters['theme/bannersColorInverted']
      });
      commit('TRIGGER_RERENDER'); // applicable upon revert change
      await new Promise(resolve => setTimeout(resolve, 250));
      commit('SET_LOADING', false);
    },
    async initializeExisting({ commit }, announcementId: number) {
      commit('SET_LOADING', true);

      try {
        const announcement = await AnnouncementController.getAnnouncementById(announcementId);
        commit('SET_ANNOUNCEMENT', announcement);
        commit('TRIGGER_RERENDER'); // applicable upon revert change
        await new Promise(resolve => setTimeout(resolve, 250));
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Failed to load announcement';
        console.error('Error initializing existing announcement:', error, message);
        throw error;
      } finally {
        commit('SET_LOADING', false);
      }
    },
    async removeAllOrganizations({ state, commit }) {
      if (!state.announcement?.id) {
        console.error("Cannot remove all organizations: Announcement id is missing.");
        throw new Error('Missing announcement id')
      }
      commit('SET_SAVING', true);

      try {
        const updatedAnnouncement = await AnnouncementController.removeAllOrganizations(state.announcement.id);
        commit('SET_ANNOUNCEMENT', updatedAnnouncement);
        commit('TRIGGER_RERENDER');
        await new Promise(resolve => setTimeout(resolve, 250));
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Failed to remove all organizations';
        console.error('Remove all organizations error:', message, error);
        throw error;
      } finally {
        commit('SET_SAVING', false);
      }
    },
    async reset({ commit }) {
      commit('RESET_STATE');
    },

    // Field Update Actions
    addTranslation({ commit }, translation: Translation) {
      commit('UPDATE_TRANSLATION', translation);
    },
    removeTranslation({ commit }, index: number) {
      commit('REMOVE_TRANSLATION', index);
    },
    setAssignedOrganizations({ commit }, orgIds: number[]) {
      commit('SET_ASSIGNED_ORGANIZATIONS', orgIds);
    },
    setBarColor({ commit }, color: string) {
      commit('UPDATE_ANNOUNCEMENT_FIELD', { field: 'barColor', value: color });
    },
    setDisplayOption({ commit }, option: AnnouncementDisplayOption | null) {
      commit('UPDATE_ANNOUNCEMENT_FIELD', { field: 'displayOption', value: option });
    },
    setEnabled({ commit }, enabled: boolean) {
      commit('UPDATE_ANNOUNCEMENT_FIELD', { field: 'enabled', value: enabled });
    },
    setExternalUrl({ commit }, url: string | null) {
      commit('SET_EXTERNAL_URL', url);
    },
    setFromDate({ commit }, date: number) {
      commit('UPDATE_ANNOUNCEMENT_FIELD', { field: 'fromDate', value: date });
    },
    setSelectedMedia({ commit }, mediaIdentifier: string | null) {
      commit('SET_SELECTED_MEDIA', mediaIdentifier);
    },
    setSelectedOrganizations({ commit }, orgIds: string[]) {
      commit('UPDATE_ANNOUNCEMENT_FIELD', { field: 'assignedOrganizations', value: orgIds });
    },
    setTextColor({ commit }, color: string) {
      commit('UPDATE_ANNOUNCEMENT_FIELD', { field: 'textColor', value: color });
    },
    setTitle({ commit }, title: string) {
      commit('UPDATE_ANNOUNCEMENT_FIELD', { field: 'title', value: title });
    },
    setToDate({ commit }, date: number|null) {
      commit('UPDATE_ANNOUNCEMENT_FIELD', { field: 'toDate', value: date });
    },
    setType({ commit }, type: AnnouncementType) {
      commit('RESET_ADDITIONAL_TYPE_DATA');
      commit('UPDATE_ANNOUNCEMENT_FIELD', { field: 'type', value: type });
    },
    setUploadedFile({ commit }, file: File | null) {
      commit('SET_UPLOADED_FILE', file);
    },
    updateTranslationTitle({ commit }, payload: { index: number, title: string }) {
      commit('UPDATE_TRANSLATION_TITLE', payload);
    },
    updateTranslationLocale({ commit }, payload: { index: number, localeId: number }) {
      commit('UPDATE_TRANSLATION_LOCALE', payload);
    },

    // Save Actions (Placeholders - require controller updates)
    async saveAnnouncement({ state, commit, getters }) {
      if (!state.announcement || !getters.canSave) {
        console.warn('Save cancelled: Invalid state or no changes.');
        throw new Error ('Save cancelled: Invalid state or no changes.');
      }

      const isUploadingFile = !!state.uploadedFile;
      commit('SET_IS_UPLOADING', isUploadingFile);
      commit('SET_SAVING', true);
      commit('SET_UPLOAD_PROGRESS', isUploadingFile ? 0 : null); // Start progress at 0 if uploading

      // Define the progress handler function (has access to commit)
      const handleUploadProgress = (progressEvent: AxiosProgressEvent) => {
        if (progressEvent.total) {
          const { percentage } = useUploadProgressComputation(progressEvent);
          commit('SET_UPLOAD_PROGRESS', percentage);
        } else {
          commit('SET_UPLOAD_PROGRESS', null); // Indicate indeterminate progress
        }
      };

      try {
        const formData = new FormData();
        const announcementDataToSend = cloneDeep(state.announcement);
        formData.append('request', new Blob([JSON.stringify(announcementDataToSend)], { type: 'application/json' }));

        if (isUploadingFile && state.uploadedFile) {
          formData.append('file', state.uploadedFile, state.uploadedFile.name);
        }
        const payload = formData;

        const isNew = !state.announcement?.id;
        const progressCallbackArg = isUploadingFile ? handleUploadProgress : undefined;
        let savedAnnouncement: AnnouncementAdminResponse;

        if (state.announcement.id) {
          savedAnnouncement = await AnnouncementController.updateAnnouncement(
            state.announcement.id,
            payload,
            progressCallbackArg
          );
        } else {
          savedAnnouncement = await AnnouncementController.createAnnouncement(
            payload,
            progressCallbackArg
          );
        }
        commit('SET_ANNOUNCEMENT', savedAnnouncement);
        commit('RESET_UPLOAD_STATE');
        commit('TRIGGER_RERENDER');

        if (isNew && savedAnnouncement.id) {
          await router.push({
            name: 'AnnouncementEditor',
            params: { id: savedAnnouncement.id.toString() }
          });
          commit('TRIGGER_RERENDER');
        }
      } catch (error) {
        console.error("Failed to save announcement:", error);
        commit('RESET_UPLOAD_STATE');
        throw error;
      } finally {
        commit('SET_SAVING', false);
      }
    }
  }
};

export default announcementEditor;
